import { v, type Infer } from "convex/values";
import { kanbanColumnToneEntry } from "../collections/kanban_settings";
import {
  internalControl,
  defineTableWithInternalControl,
} from "../../lib/shared/validators";

// ============================================================================
// CAMPOS PERSONALIZADOS (base de "Colecciones")
// ============================================================================
//
// Cada organización define campos tipados extra para tablas existentes (las
// tablas raíz de negocio: clientes, servicios, productos, cotizaciones...). La DEFINICIÓN vive en `custom_fields` (una fila por campo);
// los VALORES viven en el propio registro, en `customFields: { [key]: valor }`,
// así llegan en la misma query que la tabla/el formulario sin lecturas extra.
//
// Permisos:
//   - Configurar definiciones: módulo `custom_fields` (create/update/delete).
//   - Leer definiciones para pintar formularios/tablas: permiso de LECTURA del
//     módulo de la tabla (p. ej. `clients`), ver queries.ts.
//   - Escribir valores: las mutations de la propia tabla (permiso del módulo).

/** Tablas que admiten campos personalizados. Agregar aquí + en ENTITY_MODULE. */
export const customFieldEntityType = v.union(
  v.literal("clients"),
  v.literal("services"),
  v.literal("quotations"),
  v.literal("products"),
  v.literal("suppliers"),
  v.literal("categories"),
  v.literal("brands"),
  v.literal("sales"),
  v.literal("purchase_orders"),
  v.literal("work_orders"),
  v.literal("personnel"),
  v.literal("equipment"),
  v.literal("warehouses"),
  /**
   * Pseudo-entityType para los IDs virtuales de `collection_records`
   * (`_id` nativo de Convex, `_uuid` generado por el server). El
   * `native_field_settings` guarda filas con este entityType +
   * `collectionId` para que la decisión de visibilidad viaje
   * con la collection (no con el browser del usuario, como
   * antes, cuando solo estaba en localStorage).
   */
  v.literal("collection_record"),
);
export type CustomFieldEntityType = Infer<typeof customFieldEntityType>;

/**
 * Dueño de una definición: una tabla del sistema (`entityType`) o una colección
 * propia del usuario (`entityType: "collection"` + `collectionId`).
 */
export const customFieldOwnerType = v.union(
  customFieldEntityType,
  v.literal("collection"),
);

/** Tablas del sistema a las que puede apuntar un campo de tipo relación.
 *  `"collections"` apunta a un registro (`collection_records`) de
 *  cualquier collection propia de la empresa (no a la collection
 *  misma). El lookup del registro usa la config de la collection
 *  destino (`displayFieldKey`, `fields`). */
export const RELATION_TABLES = [
  "clients",
  "products",
  "services",
  "sellers",
  "collections",
  // Usuarios de la empresa (responsables, asignados): nombre, correo y foto.
  "users",
] as const;
export type RelationTable = (typeof RELATION_TABLES)[number];
export const customFieldRelationTable = v.union(
  v.literal("clients"),
  v.literal("products"),
  v.literal("services"),
  v.literal("sellers"),
  v.literal("collections"),
  v.literal("users"),
);

/**
 * Tipos de campo disponibles: el subconjunto de los tipos de documentos que
 * tiene sentido como dato de un registro (sin contraseña ni adjuntos, que
 * necesitan almacenamiento aparte y llegan en una fase siguiente).
 */
export const CUSTOM_FIELD_TYPES = [
  "text",
  "textarea",
  "number",
  "currency",
  "date",
  "select",
  // Selección múltiple: el usuario define `options` y el valor es un subconjunto
  // (no vacío) de ellas. Se serializa como JSON string `'["a","b"]'` (mismo
  // patrón que `image_multiple`) para no tocar `customFieldValue`, que solo
  // admite `string | number | boolean | null`.
  "multiselect",
  "checkbox",
  "url",
  "email",
  "phone",
  "image",
  "image_multiple",
  // Función JavaScript: el usuario define el código en `code` (no en
  // `defaultValue`); el resultado de evaluarla contra el `record` actual
  // es lo que se guarda en `customFields[key]`. Mismo set de valores
  // admitidos que los demás tipos (string | number | boolean | null).
  "function",
  // Ubicación: punto en el mapa, JSON string `{"lat":..,"lng":..}` (mismo
  // patrón que `multiselect`: cabe en `customFieldValue`).
  "location",
  // Lista libre: valores escritos como chips (JSON string `'["a","b"]'`, como
  // `multiselect`); `options` son sugerencias, no un límite.
  "chips",
  // Dirección: país, departamento / estado, municipio / ciudad y dirección
  // escrita (JSON string, ver address.ts), como la ubicación de clientes.
  "address",
] as const;

/** Tipos de las colecciones propias: los mismos + relación, etiquetas, búsqueda y persona. */
export const COLLECTION_FIELD_TYPES = [...CUSTOM_FIELD_TYPES, "relation", "tags", "lookup", "person"] as const;
export type CustomFieldType = (typeof COLLECTION_FIELD_TYPES)[number];

export const customFieldType = v.union(
  v.literal("text"),
  v.literal("textarea"),
  v.literal("number"),
  v.literal("currency"),
  v.literal("date"),
  v.literal("select"),
  v.literal("multiselect"),
  v.literal("checkbox"),
  v.literal("url"),
  v.literal("email"),
  v.literal("phone"),
  // Imagen única: storageId (`Id<"_storage">`) guardado como string.
  v.literal("image"),
  // Lista de imágenes: JSON string con un array no vacío de storageIds.
  v.literal("image_multiple"),
  // Función JS: ver `code` abajo. El valor guardado es el resultado de
  // evaluar la función contra el registro (en el cliente).
  v.literal("function"),
  // Ubicación: JSON string `{"lat":..,"lng":..}` (ver `CUSTOM_FIELD_TYPES`).
  v.literal("location"),
  // Lista libre: JSON string con los valores escritos (`options` sugiere).
  v.literal("chips"),
  // Dirección: JSON string `{country, state?, city?, address?}` (address.ts).
  v.literal("address"),
  // Solo en colecciones propias: id (string) de un registro de `relationTable`.
  v.literal("relation"),
  // Solo en colecciones propias: etiquetas (JSON string con los nombres,
  // `'["urgente","vip"]'`), del catálogo de etiquetas de la empresa (colores).
  v.literal("tags"),
  // Solo en colecciones propias: muestra un campo del registro relacionado
  // (`lookup`). No guarda valor: se lee al consultar.
  v.literal("lookup"),
  // Solo en colecciones propias: personas con nombre y foto, JSON string
  // `'[{"name":"Ana","image":"<storageId>"}]'` (ver `person.ts`); una sola
  // salvo `multiple`.
  v.literal("person"),
);

/**
 * Valor guardado en `registro.customFields[key]`, según el tipo:
 *   texto/selección/correo/teléfono/url/relación (id) → string
 *   número/moneda → number · fecha → timestamp ms · casilla → boolean
 */
/** Persona de la lista de un campo Persona (`ref`: id estable de la entrada). */
export const customFieldPersonOption = v.object({
  ref: v.string(),
  name: v.string(),
  image: v.optional(v.string()),
  email: v.optional(v.string()),
  phone: v.optional(v.string()),
});

/**
 * De dónde salen las personas de un campo Persona: una lista fija del campo
 * o los registros de otra colección (nombre, foto, correo y teléfono de sus
 * campos). Sin fuente, se escriben en el formulario. Con fuente, el valor
 * guarda la `ref` y se elige de la lista (vínculo vivo).
 */
const personCollectionSource = v.object({
  kind: v.literal("collection"),
  collectionId: v.id("collections"),
  nameField: v.string(),
  imageField: v.optional(v.string()),
  emailField: v.optional(v.string()),
  phoneField: v.optional(v.string()),
});
export const customFieldPersonSource = v.union(
  v.object({ kind: v.literal("list"), people: v.array(customFieldPersonOption) }),
  personCollectionSource,
);
/** Lo que se manda al guardar la fuente: las personas nuevas de la lista aún sin `ref`. */
export const customFieldPersonSourceInput = v.union(
  v.object({
    kind: v.literal("list"),
    people: v.array(
      v.object({
        ref: v.optional(v.string()),
        name: v.string(),
        image: v.optional(v.string()),
        email: v.optional(v.string()),
        phone: v.optional(v.string()),
      }),
    ),
  }),
  personCollectionSource,
);

export const customFieldValue = v.union(
  v.string(),
  v.number(),
  v.boolean(),
	v.null(),
  v.any()
);
export type CustomFieldValue = Infer<typeof customFieldValue>;

/** Validador del objeto de valores que llevan los registros. */
export const customFieldValues = v.record(v.string(), customFieldValue);
export type CustomFieldValues = Infer<typeof customFieldValues>;

/** Operaciones de un campo resumen. */
export const ROLLUP_OPS = ["sum", "count", "min", "max", "avg"] as const;
export type RollupOp = (typeof ROLLUP_OPS)[number];

/** Qué hace un campo relación cuando se elimina el registro apuntado. */
export const relationOnDelete = v.union(v.literal("restrict"), v.literal("setNull"));
export type RelationOnDelete = "restrict" | "setNull";

export const customFieldRollup = v.object({
  /**
   * Colección fuente: una hija (líneas, declarada en `children` del dueño del
   * campo) o, con `via`, cualquier colección con una relación a esta.
   */
  childCollectionId: v.id("collections"),
  /**
   * Resumen por relación: key del campo relación de la colección fuente que
   * apunta a esta. Se resumen los registros que apuntan a cada registro (por
   * las aristas de `collection_relations`). Ausente: líneas de la hija.
   */
  via: v.optional(v.string()),
  /** Campo de la hija que se agrega (no hace falta para `count`). */
  fieldKey: v.optional(v.string()),
  op: v.union(
    v.literal("sum"),
    v.literal("count"),
    v.literal("min"),
    v.literal("max"),
    v.literal("avg"),
  ),
  /**
   * Solo los registros de la fuente que cumplen todas (igualdad, como texto):
   * p. ej. el ISV de las líneas al 15 % (`impuesto` = "15") o los pagos no
   * revertidos. Ausente: todos.
   */
  where: v.optional(v.array(v.object({ fieldKey: v.string(), value: v.string() }))),
});

/**
 * Campo búsqueda (`type: "lookup"`): muestra el campo `fieldKey` del registro
 * apuntado por la relación `relationKey` de la misma colección. No se guarda
 * en `values`: se lee junto con la página (`getLookupValues`).
 */
export const customFieldLookup = v.object({
  relationKey: v.string(),
  fieldKey: v.string(),
});

/** Tipos que un campo búsqueda puede mostrar (valores simples, sin otra relación). */
export const LOOKUP_TARGET_TYPES: ReadonlySet<string> = new Set([
  "text",
  "textarea",
  "number",
  "currency",
  "date",
  "select",
  "multiselect",
  "checkbox",
  "url",
  "email",
  "phone",
  "function",
  "location",
  "tags",
  "chips",
  "address",
]);

/**
 * Unicidad de un campo de colección: el valor (o la combinación con `with`)
 * no se repite entre los registros vigentes que cumplen `when`. `message`:
 * el aviso al repetirse (por defecto, uno con los nombres de los campos).
 */
export const customFieldUnique = v.object({
  with: v.optional(v.array(v.string())),
  when: v.optional(
    v.array(v.object({ fieldKey: v.string(), operator: v.string(), value: v.optional(v.string()) })),
  ),
  message: v.optional(v.string()),
});

/** Icono de una opción de lista (ver option_icons.ts). */
export const optionIconEntry = v.object({ value: v.string(), icon: v.string() });

/** Condición sobre el registro fuente de una numeración (`numbering.source`):
 *  misma forma que las de automatizaciones (el operador se valida al guardar). */
const numberingSourceCondition = v.object({
  fieldKey: v.string(),
  operator: v.string(),
  value: v.optional(v.string()),
});

/**
 * Registro que da el número (`numbering.source`): se lee su contador, se arma
 * el código con `format` y el contador avanza (+1) en la misma transacción.
 *  - `from`: `@config.<clave>.<relación>`, el registro que apunta la
 *    configuración del alcance del documento.
 *  - `collection` + `match`: el primero de esa colección que cumple las
 *    condiciones (respaldo de `from`, o sin configuración). Valores: `@now`,
 *    `@branch` (sucursal del documento) y `{campo}` del documento.
 *  - `counter`: campo número del registro fuente.
 *  - `format`: `{campo}` del registro fuente y `{#}` / `{#:8}` (contador con
 *    ceros).
 *  - `checks`: condiciones que el registro fuente debe cumplir antes de
 *    consumir; si no, el guardado falla con `message` (`{campo}` del fuente).
 *    Valores: `@now` y `{campo}` del fuente.
 *  - `copy`: campos del fuente (`from`) que se copian al documento (`to`),
 *    de solo lectura como el número.
 */
export const numberingSource = v.object({
  from: v.optional(v.string()),
  collection: v.optional(v.string()),
  match: v.optional(v.array(numberingSourceCondition)),
  counter: v.string(),
  format: v.string(),
  checks: v.optional(v.array(v.object({ ...numberingSourceCondition.fields, message: v.string() }))),
  copy: v.optional(v.array(v.object({ from: v.string(), to: v.string() }))),
});

/** Autonumeración de un campo de texto (F3). Formato de las nomenclaturas:
 *  prefijo + separador + fecha + separador + contador con ceros. */
export const customFieldNumbering = v.object({
  prefix: v.string(),
  separator: v.optional(v.string()),
  /** "" (sin fecha), "YYYY" o "YYYYMM". */
  dateFormat: v.optional(v.union(v.literal(""), v.literal("YYYY"), v.literal("YYYYMM"))),
  digits: v.number(),
  reset: v.union(v.literal("never"), v.literal("yearly"), v.literal("monthly")),
  /** Estado (o estados) en que se asigna (máquina de estados). Sin valor: al crear. */
  assignOn: v.optional(v.union(v.string(), v.array(v.string()))),
  /**
   * El número sale de otro registro en lugar del contador propio (fase C de
   * docs/analisis-colecciones-de-configuracion.md): p. ej. el rango de CAI
   * que apunta la configuración de la sucursal. Ver `numberingSource`.
   * Prefijo, dígitos y reinicio no aplican.
   */
  source: v.optional(numberingSource),
  /** Se puede escribir el número a mano (p. ej. un preimpreso de contingencia):
   *  si llega con valor se respeta y no se consume el contador. Con `unique`
   *  en el campo, no se repite. */
  manual: v.optional(v.boolean()),
  /** Legado (quitado): la numeración fiscal atada a las tablas nativas. Se
   *  ignora; queda en el validador para los deployments que aún lo guardan. */
  fiscal: v.optional(v.any()),
});

/** Autocompletar desde una relación (F3): al elegir el registro, copia sus
 *  campos (`from`) a campos de este registro (`to`). */
export const customFieldFill = v.array(v.object({ from: v.string(), to: v.string() }));

export const validatorCustomField = v.object({
  entityType: customFieldOwnerType,
  /** Colección propia dueña del campo (cuando `entityType === "collection"`). */
  collectionId: v.optional(v.id("collections")),
  /** Slug estable (snake_case). No cambia: los valores se guardan por key. */
  key: v.string(),
  label: v.string(),
  type: customFieldType,
  required: v.boolean(),
  /** Opciones de `select`. */
  options: v.optional(v.array(v.string())),
  /** Color de cada opción (`select`/`multiselect`), como las columnas del
   *  kanban: lista `{ value, tone }` sin "neutral" (es el gris por defecto). */
  optionTones: v.optional(v.array(kanbanColumnToneEntry)),
  /** Icono (lucide) de cada opción de una lista: `{ value, icon }`. */
  optionIcons: v.optional(v.array(optionIconEntry)),
  /** Valor que no se repite en la colección (ver collections/uniqueness.ts). */
  unique: v.optional(customFieldUnique),
  /** Tabla destino de un campo `relation`. */
  relationTable: v.optional(customFieldRelationTable),
  /**
   * Para `relationTable: "collections"`: id de la collection cuyos
   * `collection_records` son destino válido del relation field. Se
   * configura en el FieldsPanel (es decisión de diseño del field,
   * no de cada valor). Obligatorio cuando `relationTable === "collections"`
   * — validado en `createCustomField`/`updateCustomField`.
   */
  relationCollectionId: v.optional(v.id("collections")),
  /**
   * Campo del registro destino que se muestra como etiqueta en la tabla y en
   * el chip del formulario (solo campos `relation`). Si no se setea o el valor
   * del campo destino está vacío, se usa la etiqueta por defecto de la tabla
   * (ver `RELATION_TABLE_FIELDS`). El valor guardado sigue siendo solo el id.
   */
  lookupField: v.optional(v.string()),
  /**
   * Relación a una colección: qué pasa con este campo cuando se elimina el
   * registro apuntado. `restrict`: no se puede eliminar mientras alguien lo
   * use; `setNull`: se vacía el campo. Ausente: la referencia queda (como
   * antes). Lo aplica el trigger de aristas (lib/records/relation_edges.ts).
   */
  onDelete: v.optional(relationOnDelete),
  /**
   * Relación múltiple (solo hacia colecciones): el valor es un arreglo de ids
   * (hasta `MAX_RELATION_IDS`), no un id. Cada id es una arista.
   */
  multiple: v.optional(v.boolean()),
  /** Valor inicial para registros nuevos (mismo formato que el editor). */
  defaultValue: v.optional(v.string()),
  /**
   * Código fuente de la función JavaScript, solo para campos `function`.
   * Se evalúa en el cliente con `new Function(...)` y el `record` actual
   * como argumento. El resultado se guarda en `customFields[key]`. Tope
   * de tamaño aplicado en el editor de campos (no en el schema, para
   * permitir migraciones de payloads grandes sin tocar el validador).
   */
  code: v.optional(v.string()),
  /**
   * Tipo de retorno declarado por el usuario para un campo `function`.
   * Es una referencia que el cliente usa para validar contra el typeof
   * del resultado runtime: si difieren, el chip/celda avisa con un
   * "Esperaba X, recibí Y" sin bloquear el guardado. Solo aplica
   * cuando `type === "function"` (en otros tipos queda `undefined`).
   * Valores: `"string"`, `"number"`, `"boolean"`. La función puede
   * devolver `null` (chip vacío) sin disparar el mismatch.
   */
  returnType: v.optional(
    v.union(
      v.literal("string"),
      v.literal("number"),
      v.literal("boolean"),
      // Texto HTML que la tabla pinta saneado (clases y estilos permitidos).
      v.literal("html"),
      // Objeto que describe una gráfica (ver lib/shared/function_chart.ts).
      v.literal("chart"),
      // Número que se muestra como moneda (totales calculados).
      v.literal("currency"),
    ),
  ),
  /**
   * `"server"`: el campo `function` se calcula en el servidor al guardar (mismo
   * código y sandbox sval) y se persiste; lo que mande el navegador se ignora.
   * Sin valor: se calcula en el navegador, como siempre.
   */
  compute: v.optional(v.literal("server")),
  /**
   * Campo resumen (número o moneda): agrega un campo de las líneas de una
   * colección hija. Se recalcula al guardar el documento y al crear, editar o
   * borrar una línea; el valor que mande el navegador se ignora.
   */
  rollup: v.optional(customFieldRollup),
  /** Campo búsqueda (solo `lookup`). */
  lookup: v.optional(customFieldLookup),
  /** Autocompletar al elegir el registro relacionado (solo `relation`). */
  fill: v.optional(customFieldFill),
  /** Autonumeración (solo `text`). */
  numbering: v.optional(customFieldNumbering),
  /** Persona: lista fija o colección de donde se eligen (solo `person`). */
  personSource: v.optional(customFieldPersonSource),
  order: v.number(),
  /** Si se muestra como columna en la tabla del módulo. */
  showInTable: v.boolean(),
  /** Si se muestra en el formulario del registro (modal o página). Sin
   *  valor: sí. Un campo obligatorio (o por regla) se muestra siempre. */
  showInForm: v.optional(v.boolean()),
  /**
   * Si el campo es filtrable en la tabla del módulo. Solo aplica a
   * `collection_records` (la única tabla donde la lista de filtros
   * visibles se elige por custom field individual). El tope por
   * tabla es `MAX_FILTERABLE_PER_COLLECTION` (ver helpers.ts) y se
   * valida server-side en `setCustomFieldFilterable`.
   *
   * Optional para tolerar documentos creados antes del feature
   * (Quitar en Fase C cuando se haya hecho el backfill).
   */
  filterable: v.optional(v.boolean()),
  /**
   * Icono de lucide (nombre del componente, p. ej. "Building2") que reemplaza
   * al icono por defecto del tipo en formularios y en el editor.
   */
  icon: v.optional(v.string()),
  internalControl,
});

export const custom_fields = defineTableWithInternalControl(
  validatorCustomField,
)
  .index("by_company_and_entity", ["internalControl.companyId", "entityType"])
  .index("by_company_and_collection", [
    "internalControl.companyId",
    "collectionId",
  ]);

/**
 * Configuración de un campo NATIVO por organización (ocultar / obligatorio),
 * dentro de lo que permite el catálogo `NATIVE_FIELDS` (native_fields.ts).
 * Una fila por campo configurado; sin fila = comportamiento por defecto.
 *
 * A partir de la Fase A del feature UUID, también se usa para los
 * system fields virtuales de `collection_records` (`_id`, `_uuid`):
 * en ese caso `entityType === "collection_record"` y `collectionId`
 * apunta a la collection dueña. El resto del tiempo `collectionId`
 * queda indefinido y `entityType` es uno de los system tables
 * (`clients`, `services`, `quotations`).
 */
export const validatorNativeFieldSetting = v.object({
  entityType: customFieldEntityType,
  /**
   * Para `entityType === "collection_record"`: id de la collection
   * dueña del setting. Para system tables queda undefined.
   * Necesario porque una organización puede tener N collections y
   * cada una tiene su propio par de `_id` / `_uuid` (visibilidad
   * independiente).
   */
  collectionId: v.optional(v.id("collections")),
  fieldKey: v.string(),
  hidden: v.boolean(),
  /** Solo si el campo permite cambiarlo; sin valor = el obligatorio por defecto. */
  required: v.optional(v.boolean()),
  internalControl,
});

export const native_field_settings = defineTableWithInternalControl(
  validatorNativeFieldSetting,
)
  .index("by_company_and_entity", ["internalControl.companyId", "entityType"])
  // Indice nuevo para los system fields de `collection_records`.
  // `(companyId, entityType, collectionId)` deja el query de
  // `getCollectionRecordFieldSettings` O(log n) sobre el subset
  // de la empresa + collection.
  .index("by_company_and_collection", [
    "internalControl.companyId",
    "entityType",
    "collectionId",
  ]);
